import { useEffect, useRef } from 'react'
import { AxiosError } from 'axios'
import {
  useInfiniteQuery as useInfiniteQueryHook,
  type UseInfiniteQueryOptions as UseInfiniteQueryBaseOptions,
  type QueryKey,
} from '@tanstack/react-query'
import useAuthentication from '@/hooks/auth/useAuthentication'

export type UseInfiniteQueryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = string,
> = Omit<
  UseInfiniteQueryBaseOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TPageParam
  >,
  'initialPageParam'
>

export default function useInfiniteQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = string,
>({
  ...options
}: UseInfiniteQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey,
  TPageParam
>) {
  const { getFreshIdToken } = useAuthentication()
  const hasRefreshed = useRef(false) // Used to prevent multiple refresh attempts

  const query = useInfiniteQueryHook<
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TPageParam
  >({
    ...options,
    initialPageParam: '' as TPageParam,
  })

  useEffect(() => {
    if (
      !hasRefreshed.current &&
      (query.error as AxiosError)?.response?.status === 401
    ) {
      hasRefreshed.current = true
      getFreshIdToken()?.catch(() => {})
    }
  }, [getFreshIdToken, query.error])

  return query
}
